"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkField = exports.getTextRecordMap = void 0;
const joi_1 = __importDefault(require("joi"));
const i18n_1 = require("../../exports/i18n");
const reselect_1 = require("reselect");
const selectors_1 = require("../../exports/store/selectors");
const types_1 = require("../../types");
const field_types_1 = require("../../types/field_types");
const field_1 = require("./field");
const stat_1 = require("./stat");
const utils_1 = require("../../model/utils");
const open_field_write_types_1 = require("../../types/open/open_field_write_types");
const validate_schema_1 = require("./validate_schema");
const text_base_field_1 = require("./text_base_field");
exports.getTextRecordMap = (0, reselect_1.createSelector)([state => state, (state, datasheetId) => (0, selectors_1.getSnapshot)(state, datasheetId)], (state, snapshot) => {
    if (!snapshot) {
        return {};
    }
    const fieldId = snapshot.meta.views[0].columns[0].fieldId;
    const field = snapshot.meta.fieldMap[fieldId];
    const textRecordMap = {};
    Object.values(snapshot.recordMap).forEach(record => {
        const value = (0, selectors_1.getCellValue)(state, snapshot, record.id, fieldId);
        const text = field_1.Field.bindContext(field, state).cellValueToString(value);
        if (text) {
            textRecordMap[text] = record.id;
        }
    });
    return textRecordMap;
});
class LinkField extends field_1.ArrayValueField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    static defaultProperty() {
        return {};
    }
    get apiMetaProperty() {
        const { limitToView, foreignDatasheetId, brotherFieldId, limitSingleRecord } = this.field.property;
        return {
            foreignDatasheetId,
            brotherFieldId,
            limitToViewId: limitToView,
            limitSingleRecord,
        };
    }
    get openValueJsonSchema() {
        return {
            title: this.field.name,
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    recordId: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_linked_record_IDs),
                    },
                    title: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_linked_record_titles),
                    }
                }
            }
        };
    }
    get statTypeList() {
        return [
            stat_1.StatType.None,
            stat_1.StatType.Empty,
            stat_1.StatType.Filled,
            stat_1.StatType.PercentEmpty,
            stat_1.StatType.PercentFilled,
        ];
    }
    get basicValueType() {
        return field_types_1.BasicValueType.Array;
    }
    get innerBasicValueType() {
        return field_types_1.BasicValueType.String;
    }
    get acceptFilterOperators() {
        return [
            types_1.FOperator.Contains,
            types_1.FOperator.DoesNotContain,
            types_1.FOperator.IsEmpty,
            types_1.FOperator.IsNotEmpty,
            types_1.FOperator.IsRepeat,
        ];
    }
    validate(value) {
        const snapshot = (0, selectors_1.getSnapshot)(this.state, this.field.property.foreignDatasheetId);
        if (!snapshot) {
            return false;
        }
        if (Array.isArray(value)) {
            return value.every(recordId => {
                if (snapshot.recordMap[recordId]) {
                    return true;
                }
                return false;
            });
        }
        return false;
    }
    validateProperty() {
        return LinkField.propertySchema.validate(this.field.property, { context: { reduxState: this.state } });
    }
    validateCellValue(cellValue) {
        return LinkField.cellValueSchema.validate(cellValue);
    }
    validateOpenWriteValue(owv) {
        return LinkField.openWriteValueSchema.validate(owv);
    }
    getForeignPrimaryField() {
        const foreignDatasheetId = this.field.property.foreignDatasheetId;
        const view = (0, selectors_1.getCurrentView)(this.state, foreignDatasheetId);
        if (!view) {
            return;
        }
        const fieldId = view.columns[0].fieldId;
        const foreignPrimaryField = (0, selectors_1.getFieldMap)(this.state, foreignDatasheetId);
        return foreignPrimaryField[fieldId];
    }
    getLinkedCellValue(recordId) {
        if (!recordId) {
            return null;
        }
        const field = this.getForeignPrimaryField();
        const snapshot = (0, selectors_1.getSnapshot)(this.state, this.field.property.foreignDatasheetId);
        if (!snapshot || !field) {
            return null;
        }
        const cellValue = (0, selectors_1.getCellValue)(this.state, snapshot, recordId, field.id);
        return cellValue;
    }
    cellValue2TextArray(cellValue) {
        return cellValue ? cellValue.map(recordId => this.getLinkedRecordCellString(recordId) || '') : [];
    }
    /**
     * @orderInCellValueSensitive {boolean} optional parameter,
     * to determine whether to only do normal sorting for the associated field, without preprocessing the cell content
     */
    compare(cellValue1, cellValue2, orderInCellValueSensitive) {
        const rowIndexMap = (0, selectors_1.getRowsIndexMap)(this.state, this.field.property.foreignDatasheetId);
        if (!orderInCellValueSensitive) {
            cellValue1 = this.sortValueByForeignRowOrder(cellValue1, rowIndexMap);
            cellValue2 = this.sortValueByForeignRowOrder(cellValue2, rowIndexMap);
        }
        return super.compare(cellValue1, cellValue2);
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        if (operator === types_1.FOperator.IsEmpty) {
            return cellValue == null;
        }
        if (operator === types_1.FOperator.IsNotEmpty) {
            return cellValue != null;
        }
        const cellTextArray = this.cellValue2TextArray(cellValue);
        const [filterValue] = conditionValue;
        switch (operator) {
            case types_1.FOperator.Contains: {
                return cellValue != null && cellTextArray.some(text => this.stringInclude(text, filterValue));
            }
            case types_1.FOperator.DoesNotContain: {
                return cellValue == null || !cellTextArray.some(text => this.stringInclude(text, filterValue));
            }
            default: {
                return super.isMeetFilter(operator, cellValue, conditionValue);
            }
        }
    }
    // Sort the relation records according to their order in view.rows specified by the relation table.
    sortValueByForeignRowOrder(value, rowIndexMap) {
        if (!value) {
            return null;
        }
        return Array.from(value).sort((id1, id2) => {
            var _a, _b;
            return ((_a = rowIndexMap.get(id1)) !== null && _a !== void 0 ? _a : 0) - ((_b = rowIndexMap.get(id2)) !== null && _b !== void 0 ? _b : 0);
        });
    }
    stdValueToCellValue(stdValue) {
        // If it is data from the current field, write it directly.
        if (stdValue.data[0] && stdValue.data[0].datasheetId === this.field.property.foreignDatasheetId) {
            return stdValue.data.map(val => {
                return val.recordId;
            });
        }
        // Try to find the same value as the primary key text from the association table, if found, create the association.
        const textRecordMap = (0, exports.getTextRecordMap)(this.state, this.field.property.foreignDatasheetId);
        let texts = [stdValue.data.map(d => d.text).join(', ')];
        // Determine whether the current configuration item allows adding multiple associated records, 
        // and if it is allowed, cut it according to ","
        if (!this.field.property.limitSingleRecord) {
            const splitText = texts[0].split(/, ?/);
            if (splitText.length > 1) {
                // Both before and after the split are added to the candidate array to try to match
                texts = [texts[0], ...splitText];
            }
        }
        const cellValue = texts.reduce((pre, text) => {
            const recordId = textRecordMap[text];
            if (recordId) {
                pre.push(recordId);
            }
            return pre;
        }, []);
        return cellValue.length ? Array.from(new Set(cellValue)) : null;
    }
    cellValueToStdValue(recordIds) {
        const stdVal = {
            sourceType: field_types_1.FieldType.Link,
            data: [],
        };
        if (recordIds) {
            stdVal.data = recordIds.map(recordId => {
                const text = this.getLinkedRecordCellString(recordId) || (0, i18n_1.t)(i18n_1.Strings.record_unnamed);
                return {
                    text,
                    recordId,
                    datasheetId: this.field.property.foreignDatasheetId,
                };
            });
        }
        return stdVal;
    }
    /**
     * Obtains the content of the first column in the associated table through the `recordId` recorded in the associated field cell,
     * and the content is processed by cellValue2String.
     * @param {(string[] | null)} recordIds
     * @returns {(string[] | null)}
     */
    cellValueToArray(recordIds) {
        if (!this.validate(recordIds)) {
            return null;
        }
        const valueArray = recordIds.map(recordId => {
            return this.getLinkedRecordCellString(recordId) || (0, i18n_1.t)(i18n_1.Strings.record_unnamed);
        });
        return valueArray;
    }
    /**
     * @description concatenates the strings obtained by recordId.
     * Several records may be associated with one associated cell at the same time
     * @param {(any[] | null)} cellValues
     * @returns
     */
    arrayValueToString(cellValues) {
        return cellValues && cellValues.length ? cellValues.join(', ') : null;
    }
    cellValueToString(recordIds) {
        if (!recordIds) {
            return null;
        }
        return this.arrayValueToString(this.cellValueToArray([recordIds].flat()));
    }
    getLinkedRecordCellString(recordId) {
        const field = this.getForeignPrimaryField();
        if (!field) {
            return null;
        }
        return field_1.Field.bindContext(field, this.state).cellValueToString(this.getLinkedCellValue(recordId));
    }
    defaultValueForCondition(_condition) {
        return null;
    }
    cellValueToApiStandardValue(recordIds) {
        return recordIds;
    }
    cellValueToApiStringValue(recordIds) {
        return this.cellValueToString(recordIds);
    }
    cellValueToOpenValue(recordIds) {
        if (!this.validate(recordIds)) {
            return null;
        }
        const valueArray = recordIds.map(recordId => {
            return {
                recordId,
                title: this.getLinkedRecordCellString(recordId) || (0, i18n_1.t)(i18n_1.Strings.record_unnamed)
            };
        });
        return valueArray;
    }
    openWriteValueToCellValue(openWriteValue) {
        if ((0, utils_1.isNullValue)(openWriteValue)) {
            return null;
        }
        const isSimple = openWriteValue.length && typeof openWriteValue[0] === 'string';
        return (isSimple ? openWriteValue : openWriteValue.map(v => v.recordId));
    }
    get openFieldProperty() {
        const { limitToView: limitToViewId, foreignDatasheetId, brotherFieldId, limitSingleRecord } = this.field.property;
        return {
            foreignDatasheetId,
            brotherFieldId,
            limitToViewId,
            limitSingleRecord,
        };
    }
    validateUpdateOpenProperty(updateProperty) {
        return LinkField.openUpdatePropertySchema.validate(updateProperty);
    }
    validateOpenAddFieldProperty(addProperty) {
        return LinkField.openAddPropertySchema.validate(addProperty);
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { foreignDatasheetId, limitToViewId: limitToView, limitSingleRecord } = openFieldProperty;
        return {
            foreignDatasheetId,
            limitToView,
            limitSingleRecord
        };
    }
    filterValueToOpenFilterValue(value) {
        return text_base_field_1.TextBaseField._filterValueToOpenFilterValue(value);
    }
    openFilterValueToFilterValue(value) {
        return text_base_field_1.TextBaseField._openFilterValueToFilterValue(value);
    }
    validateOpenFilterValue(value) {
        return text_base_field_1.TextBaseField._validateOpenFilterValue(value);
    }
}
exports.LinkField = LinkField;
LinkField.propertySchema = joi_1.default.object({
    foreignDatasheetId: joi_1.default.custom((foreignDatasheetId, helpers) => {
        var _a;
        const foreignDatasheet = (0, selectors_1.getDatasheet)((_a = helpers.prefs['context']) === null || _a === void 0 ? void 0 : _a['reduxState'], foreignDatasheetId);
        // If foreignDatasheet is not loaded, the relationship has been destroyed.
        if (!foreignDatasheet) {
            return helpers.message({ en: (0, i18n_1.t)(i18n_1.Strings.link_data_error) });
        }
        return foreignDatasheetId;
    }).required(),
    brotherFieldId: joi_1.default.string().pattern(/^fld.+/, 'fieldId'),
    limitToView: joi_1.default.string().pattern(/^viw.+/, 'viewId'),
    limitSingleRecord: joi_1.default.boolean(),
}).required();
LinkField.cellValueSchema = joi_1.default.array().items(joi_1.default.string().pattern(/^rec.+/).required()).allow(null).required();
LinkField.openWriteValueSchema = joi_1.default.array().custom((value, helpers) => {
    if (value.map((item) => (/^rec.+/.test(item.recordId || item)))) {
        return value;
    }
    return helpers.error('valid date');
}).allow(null).required();
LinkField.openUpdatePropertySchema = joi_1.default.object({
    foreignDatasheetId: joi_1.default.string().required(),
    limitToViewId: joi_1.default.string().pattern(/^viw.+/, 'viewId'),
    limitSingleRecord: joi_1.default.boolean(),
    conversion: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(open_field_write_types_1.Conversion))
}).required();
LinkField.openAddPropertySchema = joi_1.default.object({
    foreignDatasheetId: joi_1.default.string().required(),
    limitToViewId: joi_1.default.string().pattern(/^viw.+/, 'viewId'),
    limitSingleRecord: joi_1.default.boolean()
}).required();
//# sourceMappingURL=link_field.js.map